import React, { useRef, useState, FormEvent, ChangeEvent } from 'react';
import styled from 'styled-components';
import { theme, media } from './theme';
import emailjs from '@emailjs/browser';
import bgImage from './assets/graphics/bg.jpg';
import { useTranslation } from 'react-i18next';

import Header from './components/Header';
import { ReactComponent as InfoIcon } from './assets/icons/info.svg';
import { ACCOUNT_TYPES } from './constants';
import { useAudioPlayer } from './hooks/useAudioPlayer';

// Styled components
const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: calc(100svh - ${theme.spacing.controlBarHeight}px);
  display: flex;
  flex-direction: column;
  color: ${theme.colors.gold};
  box-sizing: border-box;
  padding: 60px 50px ${theme.spacing.lg} 50px;
  background-image: url(${bgImage});
  background-size: cover;
  background-position: center;
  overflow-y: auto;
  overflow-x: hidden;

  ${media.tablet} {
    padding: ${theme.spacing.xl} ${theme.spacing.lg} ${theme.spacing.lg} ${theme.spacing.lg};
  }
`;

const FormTitle = styled.h1`
  border-left: 2px solid ${theme.colors.gold};
  padding-left: 2rem;
  font-size: ${theme.fontSizes.mega};
  font-weight: ${theme.fontWeights.bold};
  margin-bottom: ${theme.spacing.xxxl};
`;

const FormBody = styled.div`
  flex: 1;
  display: flex;
  padding: 0 2rem 2rem;
  gap: 2rem;

  ${media.desktopMd} {
    flex-direction: column;
    padding: 0 1rem 1rem;
  }
`;

const Description = styled.div`
  flex: 1;
  padding-right: 2rem;
  color: ${theme.colors.white};
  line-height: ${theme.lineHeights.normal};
  font-size: ${theme.fontSizes.lg};

  ${media.desktopMd} {
    padding-right: 0;
    font-size: ${theme.fontSizes.sm};
  }

  p {
    margin: 0;
  }
`;

const FormPanel = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const FormRow = styled.div<{ isCheckbox?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${props => props.isCheckbox && `
    display: block;
  `}

  ${media.desktopMd} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FormLabel = styled.label<{ isCheckbox?: boolean }>`
  flex: 0 0 120px;
  font-size: ${theme.fontSizes.base};

  ${props => props.isCheckbox && `
    padding-left: ${theme.spacing.lg};
  `}

  ${media.desktopMd} {
    margin-bottom: 0.5rem;
    flex: 0;
  }

  a {
    font-weight: ${theme.fontWeights.bold};
    color: ${theme.colors.gold};
  }
`;

const FormInput = styled.input<{ isCheckbox?: boolean }>`
  flex: 1;
  background: transparent;
  border: none;
  border-bottom: 1px dashed ${theme.colors.gold};
  color: ${theme.colors.white};
  font-size: ${theme.fontSizes.base};
  padding: 0.25rem;
  outline: none;

  ${props => props.isCheckbox && `
    width: auto;
  `}

  ${media.desktopMd} {
    width: 100%;
  }
`;

const InfoRow = styled(FormRow)`
  display: flex;
  align-items: center;
  position: relative;
  justify-content: space-between;
  width: 100%;

  > div {
    display: flex;
    flex: 1;

    ${media.desktopMd} {
      width: 100%;
    }
  }
`;

const SelectContainer = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;

  &::after {
    content: "▼";
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: ${theme.colors.gold};
    font-size: ${theme.fontSizes.xs};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  background: transparent;
  border: 1px solid ${theme.colors.gold};
  color: ${theme.colors.gold};
  font-weight: ${theme.fontWeights.bold};
  font-size: ${theme.fontSizes.base};
  appearance: none;
`;

const FormActions = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: flex-start;
  gap: 2rem;

  ${media.desktopMd} {
    padding-top: ${theme.spacing.lg};
  }
`;

const FAQButton = styled.button`
  width: 100%;
  background: ${theme.colors.overlayDark};
  border: 1px solid ${theme.colors.gold};
  color: ${theme.colors.gold};
  padding: ${theme.spacing.lg};
  font-size: ${theme.fontSizes.md};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: background ${theme.transitions.normal};

  &:hover {
    background: ${theme.colors.goldDark};
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: ${theme.colors.cardBg};
  border: 1px solid ${theme.colors.gold};
  color: ${theme.colors.gold};
  padding: ${theme.spacing.lg};
  font-size: ${theme.fontSizes.md};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: background ${theme.transitions.normal};

  &:hover {
    background: ${theme.colors.goldDark};
  }
`;

const ProgressOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${theme.colors.overlayDark};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${theme.zIndex.modal};
`;

const ProgressModal = styled.div`
  background: ${theme.colors.bgDark};
  padding: 1rem 2rem;
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.white};
  font-size: 1.2rem;
  text-align: center;
`;

const SentMessage = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 1.25rem;
  color: ${theme.colors.gold};
`;

/**
 * Mapování jazyka, planName a selectedAvatar na URL MP3 souborů
 */
const AUDIO_URL_MAP: Record<string, Record<string, Record<string, string>>> = {
  cs: {
    Gold: {
      Robert: 'https://isprodukcefiregold.blob.core.windows.net/videos/MUZ_DODELAVKA_2.mp3',
      Silvie: 'https://isprodukcefiregold.blob.core.windows.net/videos/ZENA_DODELAVKA_2.mp3'
    },
    Max: {
      Robert: 'https://isprodukcefiregold.blob.core.windows.net/videos/MUZ_DODELAVKA_4.mp3',
      Silvie: 'https://isprodukcefiregold.blob.core.windows.net/videos/ZENA_DODELAVKA_4.mp3'
    },
    Rentier: {
      Robert: 'https://isprodukcefiregold.blob.core.windows.net/videos/MUZ_DODELAVKA_2.mp3',
      Silvie: 'https://isprodukcefiregold.blob.core.windows.net/videos/ZENA_DODELAVKA_2.mp3'
    }
  },
  sk: {
    Gold: {
      Robert: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/MUZ_DODELAVKA_2.mp3',
      Silvie: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/ZENA_DODELAVKA_2.mp3'
    },
    Max: {
      Robert: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/MUZ_DODELAVKA_4.mp3',
      Silvie: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/ZENA_DODELAVKA_4.mp3'
    },
    Rentier: {
      Robert: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/MUZ_DODELAVKA_2.mp3',
      Silvie: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/ZENA_DODELAVKA_2.mp3'
    }
  },
  en: {
    Gold: {
      Robert: 'https://isprodukcefiregold.blob.core.windows.net/videos/EN/MUZ_DODELAVKA_2.mp3',
      Silvie: 'https://isprodukcefiregold.blob.core.windows.net/videos/EN/ZENA_DODELAVKA_2.mp3'
    },
    Max: {
      Robert: 'https://isprodukcefiregold.blob.core.windows.net/videos/EN/MUZ_DODELAVKA_4.mp3',
      Silvie: 'https://isprodukcefiregold.blob.core.windows.net/videos/EN/ZENA_DODELAVKA_4.mp3'
    },
    Rentier: {
      Robert: 'https://isprodukcefiregold.blob.core.windows.net/videos/EN/MUZ_DODELAVKA_2.mp3',
      Silvie: 'https://isprodukcefiregold.blob.core.windows.net/videos/EN/ZENA_DODELAVKA_2.mp3'
    }
  }
};

/**
 * Struktura formuláře
 */
interface FormData {
  contractType: string;
  firstName: string;
  lastName: string;
  ico: string;
  accountHolder: string;
  address: string;
  account: string;
  phone: string;
  email: string;
}

/**
 * Props pro ContactForm komponentu
 */
interface ContactFormProps {
  /** Varianta smlouvy (Gold/Rentier/Max) */
  contractVariant: string;
  /** Vybraný avatar (Robert/Silvie) */
  selectedAvatar: string;
  /** Callback volaný po úspěšném odeslání formuláře */
  onSubmit?: () => void;
  /** Callback volaný při kliknutí na FAQ */
  onShowFAQ?: () => void;
  /** Zdrojová cesta pro tracking */
  sourcePath: string;
}

/**
 * Komponenta kontaktního formuláře pro otevření zlatého účtu
 */
export default function ContactForm({ contractVariant, selectedAvatar, onSubmit, onShowFAQ, sourcePath }: ContactFormProps): React.ReactElement {
  const { t, i18n } = useTranslation(['forms', 'common']);
  const formRef = useRef<HTMLFormElement>(null);
  const [planName] = useState(contractVariant);

  const [form, setForm] = useState<FormData>({
    contractType: '',
    firstName: '',
    lastName: '',
    ico: '',
    accountHolder: '',
    address: '',
    account: '',
    phone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Use audio player hook with language support
  // Extract language code (e.g., 'sk' from 'sk-SK', 'en' from 'en-US')
  const currentLanguage = (i18n.language || 'cs').split('-')[0];
  const audioUrl = AUDIO_URL_MAP[currentLanguage]?.[planName]?.[selectedAvatar];

  // Debug logging
  console.log('ContactForm Audio Debug:', {
    language: i18n.language,
    currentLanguage,
    planName,
    selectedAvatar,
    audioUrl
  });

  const { toggle: toggleAudio, stop: stopAudio } = useAudioPlayer({ audioUrl });

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    stopAudio();

    if (!termsAccepted) {
      alert(t('contactForm.validation.gdprRequired'));
      return;
    }

    setIsSubmitting(true);

    if (!formRef.current) {
      setIsSubmitting(false);
      return;
    }

    emailjs
      .sendForm(
        'service_6x99yxh',
        'template_9bg985c',
        formRef.current,
        'fWkAlUO_qyEgz2hG9'
      )
      .then(
        () => {
          setIsSubmitting(false);
          setIsSent(true);
          setForm({ contractType: '', firstName: '', lastName: '', ico: '', account: '', phone: '', email: '', address: '', accountHolder: '' });
          setTermsAccepted(false);
          onSubmit && onSubmit();
        },
        (err) => {
          setIsSubmitting(false);
          alert(t('contactForm.status.error', { error: err.text }));
        }
      );
  };

  // Zpracuje klik na FAQ: zastaví audio a zavolá onShowFAQ
  const handleShowFAQ = (): void => {
    stopAudio();
    onShowFAQ && onShowFAQ();
  };

  return (
    <Container>
      {isSubmitting && (
        <ProgressOverlay>
          <ProgressModal>
            <p>{t('contactForm.status.submitting')}</p>
          </ProgressModal>
        </ProgressOverlay>
      )}

      <Header>
        <FormTitle>{t('contactForm.title', { planName })}</FormTitle>
      </Header>

      <FormBody>
        <Description>
          <p
            style={{ display: 'inline' }}
            dangerouslySetInnerHTML={{
              __html: t(`contactForm.description.${planName === ACCOUNT_TYPES.MAX ? 'business' : 'personal'}`, { planName })
            }}
          />
          <button
            type="button"
            className="btn-icon"
            onClick={toggleAudio}
            disabled={isSubmitting}
            aria-label="Přehrát informace o formátu účtu"
          >
            <InfoIcon className="info-icon" style={{ fill: '#A89B6F', width: 24, height: 24 }} />
          </button>
        </Description>

        {!isSent ? (
          <FormPanel ref={formRef} onSubmit={handleSubmit}>

            <FormRow>
              <FormLabel htmlFor="contractType">{t('contactForm.labels.contractType')}</FormLabel>
              <SelectContainer>
                <Select
                  id="contractType"
                  name="contractType"
                  value={form.contractType}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                >
                  <option value="" disabled>
                    {t('contactForm.placeholders.selectContractType')}
                  </option>
                  {planName.toLowerCase() !== 'max' && (
                    <option value={t(`contactForm.contractTypes.${planName.toLowerCase()}_70_30`)}>
                      {t(`contactForm.contractTypes.${planName.toLowerCase()}_70_30`)}
                    </option>
                  )}
                  <option value={t(`contactForm.contractTypes.${planName.toLowerCase()}_100_0`)}>
                    {t(`contactForm.contractTypes.${planName.toLowerCase()}_100_0`)}
                  </option>
                </Select>
              </SelectContainer>
            </FormRow>

            {planName === ACCOUNT_TYPES.MAX && (
              <FormRow>
                <FormLabel>{t('contactForm.labels.accountManager')}</FormLabel>
                <FormInput
                  name="accountHolder"
                  value={form.accountHolder}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </FormRow>
            )}
            {planName !== ACCOUNT_TYPES.MAX && (
              <FormRow>
                <FormLabel>{t('contactForm.labels.firstName')}</FormLabel>
                <FormInput
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </FormRow>
            )}
            {planName !== ACCOUNT_TYPES.MAX && (
              <FormRow>
                <FormLabel>{t('contactForm.labels.lastName')}</FormLabel>
                <FormInput
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </FormRow>
            )}
            {planName !== ACCOUNT_TYPES.MAX ? (
              <FormRow>
                <FormLabel>{t('contactForm.labels.birthDate')}</FormLabel>
                <FormInput
                  name="ico"
                  value={form.ico}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </FormRow>
            ) : (
              <FormRow>
                <FormLabel>{t('contactForm.labels.ico')}</FormLabel>
                <FormInput
                  name="ico"
                  value={form.ico}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </FormRow>
            )}
            {planName !== ACCOUNT_TYPES.MAX && (
              <FormRow>
                <FormLabel>{t('contactForm.labels.address')}</FormLabel>
                <FormInput
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </FormRow>
            )}
            <InfoRow>
              {planName !== ACCOUNT_TYPES.MAX ? (
                <FormLabel>{t('contactForm.labels.personalBankAccount')}</FormLabel>
              ) : (
                <FormLabel>{t('contactForm.labels.businessBankAccount')}</FormLabel>
              )}
              <div>
                <FormInput
                  name="account"
                  value={form.account}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="btn-icon"
                  data-tooltip={t('contactForm.tooltips.bankAccount')}
                  disabled={isSubmitting}
                >
                  <InfoIcon className="info-icon" style={{ fill: '#A89B6F', width: 24, height: 24 }} />
                </button>
              </div>
            </InfoRow>

            <FormRow>
              <FormLabel>{t('contactForm.labels.phone')}</FormLabel>
              <FormInput
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </FormRow>
            <FormRow>
              <FormLabel>{t('contactForm.labels.email')}</FormLabel>
              <FormInput
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </FormRow>

            <FormRow isCheckbox>
              <FormInput
                isCheckbox
                type="checkbox"
                id="termsAccepted"
                checked={termsAccepted}
                onChange={e => setTermsAccepted(e.target.checked)}
                disabled={isSubmitting}
                required
              />
              <FormLabel isCheckbox htmlFor="termsAccepted">
                <a href={t('forms:contactForm.gdpr.link')} target='_blank' rel="noopener noreferrer">{t('forms:contactForm.gdpr.text')}</a>
              </FormLabel>
            </FormRow>

            <input name="planName" value={planName} hidden readOnly />
            <input name="sourcePath" value={sourcePath} hidden readOnly />
            <input name="email_to" value="dominik.branka@blogic.cz" hidden readOnly />

            <FormActions>
              <FAQButton
                type="button"
                onClick={handleShowFAQ}
                disabled={isSubmitting}
              >
                {t('common:navigation.qa')}
              </FAQButton>
              <SubmitButton
                type="submit"
                disabled={isSubmitting}
              >
                {t('common:buttons.send')}
              </SubmitButton>
            </FormActions>
          </FormPanel>
        ) : (
          <SentMessage>
            <p>{t('forms:contactForm.status.success')}</p>
            <FormActions>
              <FAQButton
                type="button"
                onClick={handleShowFAQ}
                disabled={isSubmitting}
              >
                {t('common:navigation.qa')}
              </FAQButton>
            </FormActions>
          </SentMessage>
        )}
      </FormBody>
    </Container>
  );
}
